<template>
  <v-card>
    <v-card-text>
      <v-data-table
        locale="fr-FR"
        :page="options.page"
        :pageCount="numberOfPages"
        :headers="headers"
        :items="items"
        :sort-by="'table_size_today'"
        :sort-desc="true"
        :options.sync="options"
        :loading="loading"
        class="elevation-1"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
        :items-per-page="options.itemsPerPage"
      >
        <template v-slot:item.table_size_today="{ item }">
          <span>{{ formatBytes(item.table_size_today) }}</span>
        </template>
        <template v-slot:item.table_size_yesterday="{ item }">
          <span>{{ formatBytes(item.table_size_yesterday) }}</span>
        </template>
        <template v-slot:item.table_size_diff="{ item }">
          <span
            style="font-weight: bold"
            :class="{
              'success--text': item.table_size_diff > 0,
              'text-red': item.table_size_diff < 0,
            }"
          >
            {{ formatBytes(item.table_size_diff) }}
          </span>
        </template>
        <template v-slot:item.num_rows_today="{ item }">
          <HoverTooltip>
            <template v-slot:tooltip>
              <div>{{ formatNumber(item.num_rows_today) }} lignes</div>
            </template>
            <span>{{ formatNumberCompact(item.num_rows_today) }}</span>
          </HoverTooltip>
        </template>
        <template v-slot:item.num_rows_yesterday="{ item }">
          <HoverTooltip>
            <template v-slot:tooltip>
              <div>{{ formatNumber(item.num_rows_yesterday) }} lignes</div>
            </template>
            <span>{{ formatNumberCompact(item.num_rows_yesterday) }}</span>
          </HoverTooltip>
        </template>
        <template v-slot:item.num_rows_diff="{ item }">
          <HoverTooltip>
            <template v-slot:tooltip>
              <div>{{ formatNumber(item.num_rows_diff) }} lignes</div>
            </template>
            <span
              style="font-weight: bold"
              :class="{
                'success--text': item.num_rows_diff > 0,
                'text-red': item.num_rows_diff < 0,
              }"
            >
              {{ formatNumberCompact(item.num_rows_diff) }}
            </span>
          </HoverTooltip>
        </template>
      </v-data-table>
    </v-card-text>
    <v-dialog v-model="dialogModel.dialog" width="70vw">
      <del-confirmation
        :dialogOpen="dialogModel.dialog"
        :id="dialogModel.id"
        :name="dialogModel.entryName"
        @openDialog="openDialog"
      ></del-confirmation>
    </v-dialog>
  </v-card>
</template>
<style scoped></style>
<script>
import axios from "@axios";
import DelConfirmation from "@/components/rmr-analytics/modals/ProjectDelConfirmation.vue";
import { mdiPencil, mdiTrashCan } from "@mdi/js";
import debounce from "lodash/debounce";
import {
  formatBytes,
  formatNumberCompact,
  formatNumber,
} from "@/utils/formatting";
import HoverTooltip from "@/components/common/HoverTooltip.vue";

export default {
  name: "TablesStatsTable",
  components: { HoverTooltip, DelConfirmation },
  data() {
    return {
      search: "",
      numberOfPages: 0,
      items: [],
      loading: true,
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        total: 0,
      },
      dialogModel: {
        dialog: false,
        id: -1,
        entryName: "",
      },
      headers: [
        {
          text: "Nom de la table",
          value: "table_name",
          sortable: true,
          align: "start",
        },
        {
          text: "Taille de la table aujourd'hui",
          value: "table_size_today",
          sortable: true,
        },
        {
          text: "Taille de la table hier",
          value: "table_size_yesterday",
          sortable: true,
        },
        {
          text: "Différence de taille",
          value: "table_size_diff",
          sortable: true,
        },
        {
          text: "Nombre de lignes aujourd'hui",
          value: "num_rows_today",
          sortable: true,
        },
        {
          text: "Nombre de lignes hier",
          value: "num_rows_yesterday",
          sortable: true,
        },
        {
          text: "Différence de lignes",
          value: "num_rows_diff",
          sortable: true,
        },
      ],
      icons: {
        mdiPencil,
        mdiTrashCan,
      },
    };
  },
  watch: {
    options: {
      handler() {
        this.debouncedUpdateData();
      },
    },
    search() {
      this.options.page = 1;
      this.debouncedUpdateData();
    },
  },
  methods: {
    formatBytes,
    formatNumberCompact,
    formatNumber,
    debouncedUpdateData: debounce(async function () {
      await this.updateData();
    }, 500),
    formatTableStats(data) {
      const groupedByTable = new Map();

      data.forEach((entry) => {
        const tableName = entry.table_name;
        if (!groupedByTable.has(tableName)) {
          groupedByTable.set(tableName, []);
        }
        groupedByTable.get(tableName).push({
          ...entry,
          parsedDate: new Date(entry.date),
        });
      });
      const formattedResult = [];
      for (const entries of groupedByTable.values()) {
        entries.sort((a, b) => b.parsedDate - a.parsedDate);
        const todayData = entries[0];
        if (!todayData) continue;
        const yesterdayData = entries[1] || null;
        const numRowsDiff = yesterdayData
          ? todayData.num_rows - yesterdayData.num_rows
          : null;
        const tableSizeDiff = yesterdayData
          ? todayData.table_size - yesterdayData.table_size
          : null;
        formattedResult.push({
          table_name: todayData.table_name,
          num_rows_today: todayData.num_rows,
          table_size_today: todayData.table_size,
          num_rows_yesterday: yesterdayData ? yesterdayData.num_rows : null,
          table_size_yesterday: yesterdayData ? yesterdayData.table_size : null,
          num_rows_diff: numRowsDiff,
          table_size_diff: tableSizeDiff,
        });
      }
      return formattedResult;
    },
    async updateData() {
      this.loading = true;
      const { data } = await axios.get("/tables-stats/last-day");
      this.loading = false;
      this.items = data.items;
      this.items = this.formatTableStats(this.items);
      this.total = data.total;
      this.numberOfPages = data.total / this.options.itemsPerPage;
    },
    truncate(input) {
      return input.length > 5 ? `${input.substring(0, 30)}...` : input;
    },
    openDialog(value) {
      this.dialogModel.dialog = value;
    },
  },
};
</script>
<style lang="scss" scoped>
.filter-with-ga-id {
  max-width: 300px;
}
.above-table {
  width: 100%;
}
.search-by-site-id {
  width: 100px;
}
</style>
